export const API_BASE = process.env.NEXT_PUBLIC_API_URL ?? "";

async function request(path: string, options?: RequestInit) {
  const { headers: optHeaders, ...restOptions } = options ?? {};
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { "Content-Type": "application/json", ...optHeaders },
    ...restOptions,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    const e: any = new Error(typeof err.detail === "string" ? err.detail : "Request failed");
    e.status = res.status;
    throw e;
  }
  return res.json();
}

export const ADMIN_TOKEN = "Flint.ly";

export const api = {
  auth: {
    login: (username: string, password: string) =>
      request("/api/auth/login", { method: "POST", body: JSON.stringify({ username, password }) }),
  },
  admin: {
    listUsers: () =>
      request("/api/admin/users", { headers: { "x-admin-token": ADMIN_TOKEN } }),
    createUser: (username: string, password: string) =>
      request("/api/admin/users", { method: "POST", headers: { "x-admin-token": ADMIN_TOKEN }, body: JSON.stringify({ username, password }) }),
    updateUser: (username: string, password: string) =>
      request(`/api/admin/users/${encodeURIComponent(username)}`, { method: "PATCH", headers: { "x-admin-token": ADMIN_TOKEN }, body: JSON.stringify({ password }) }),
    deleteUser: (username: string) =>
      request(`/api/admin/users/${encodeURIComponent(username)}`, { method: "DELETE", headers: { "x-admin-token": ADMIN_TOKEN } }),
    searchCampaigns: (q: string) =>
      request(`/api/admin/campaigns?q=${encodeURIComponent(q)}`, { headers: { "x-admin-token": ADMIN_TOKEN } }),
    deleteCampaign: (id: string) =>
      request(`/api/admin/campaigns/${encodeURIComponent(id)}`, { method: "DELETE", headers: { "x-admin-token": ADMIN_TOKEN } }),
    creatorStats: () =>
      request("/api/admin/creators/stats", { headers: { "x-admin-token": ADMIN_TOKEN } }),
    bulkUploadCreators: async (file: File) => {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`${API_BASE}/api/admin/creators/bulk-upload`, {
        method: "POST",
        headers: { "x-admin-token": ADMIN_TOKEN },
        body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: res.statusText }));
        throw new Error(err.detail || "Upload failed");
      }
      return res.json();
    },
    creatorTemplateUrl: () => `${API_BASE}/api/admin/creators/template`,
  },
  campaigns: {
    list: (owner?: string) => request(`/api/campaigns${owner ? `?owner=${encodeURIComponent(owner)}` : ""}`),
    get: (id: string) => request(`/api/campaigns/${id}`),
    create: (name: string, vertical?: string, owner?: string) =>
      request("/api/campaigns", { method: "POST", body: JSON.stringify({ name, vertical, owner }) }),
    update: (id: string, fields: { name?: string; vertical?: string }) =>
      request(`/api/campaigns/${id}`, { method: "PATCH", body: JSON.stringify(fields) }),
    intelligence: (id: string) =>
      request(`/api/campaigns/${id}/intelligence`, { method: "POST" }),
  },
  brief: {
    submit: (campaignId: string, raw_input: string) =>
      request(`/api/campaigns/${campaignId}/brief`, {
        method: "POST",
        body: JSON.stringify({ raw_input }),
      }),
    get: (campaignId: string) => request(`/api/campaigns/${campaignId}/brief`),
    confirm: (campaignId: string, structured: object) =>
      request(`/api/campaigns/${campaignId}/brief/confirm`, {
        method: "PATCH",
        body: JSON.stringify({ structured }),
      }),
    analyzeGaps: (campaignId: string) =>
      request(`/api/campaigns/${campaignId}/brief/analyze-gaps`, { method: "POST" }),
    setCampaignType: (
      campaignId: string,
      payload: { primary_type: string; components?: Record<string, boolean>; whitelisting?: boolean | null },
    ) =>
      request(`/api/campaigns/${campaignId}/brief/campaign-type`, {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    warm: (campaignId: string, raw_input: string) =>
      request(`/api/campaigns/${campaignId}/brief/warm`, {
        method: "POST",
        body: JSON.stringify({ raw_input }),
      }).catch(() => {}),  // fire-and-forget, ignore errors
    refine: (campaignId: string, additions: string) =>
      request(`/api/campaigns/${campaignId}/brief/refine`, {
        method: "POST",
        body: JSON.stringify({ additions }),
      }),
    extractPdf: async (campaignId: string, file: File): Promise<{ text: string; pages: number }> => {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`${API_BASE}/api/campaigns/${campaignId}/brief/extract-pdf`, {
        method: "POST",
        body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: res.statusText }));
        throw new Error(err.detail || "PDF extraction failed");
      }
      return res.json();
    },
  },
  shortlist: {
    run: (campaignId: string, overrides = {}) =>
      request(`/api/campaigns/${campaignId}/discovery/search`, {
        method: "POST",
        body: JSON.stringify(overrides),
      }),
    get: (campaignId: string) => request(`/api/campaigns/${campaignId}/shortlist`),
    update: (campaignId: string, entryId: string, status: string, reason?: string) =>
      request(`/api/campaigns/${campaignId}/shortlist/${entryId}`, {
        method: "PATCH",
        body: JSON.stringify({ status, override_reason: reason }),
      }),
    getVideo: (campaignId: string, entryId: string) =>
      request(`/api/campaigns/${campaignId}/shortlist/${entryId}/video`),
    getEntry: (campaignId: string, entryId: string) =>
      request(`/api/campaigns/${campaignId}/shortlist/${entryId}`),
    runAssessment: (campaignId: string, entryId: string) =>
      request(`/api/campaigns/${campaignId}/shortlist/${entryId}/run-assessment`, { method: "POST" }),
    useDemo: (campaignId: string, entryId: string, blob: string) =>
      request(`/api/campaigns/${campaignId}/shortlist/${entryId}/use-demo`, {
        method: "POST",
        body: JSON.stringify({ blob }),
      }),
    uploadVideo: async (campaignId: string, entryId: string, file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API_BASE}/api/campaigns/${campaignId}/shortlist/${entryId}/upload-video`, {
        method: "POST",
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: res.statusText }));
        throw new Error(err.detail || "Upload failed");
      }
      return res.json();
    },
    submitUrl: (campaignId: string, entryId: string, video_url: string) =>
      request(`/api/campaigns/${campaignId}/shortlist/${entryId}/submit-url`, {
        method: "POST",
        body: JSON.stringify({ video_url }),
      }),
  },
  demoVideos: {
    list: () => request("/api/campaigns/demo-videos"),
  },
  reviews: {
    submit: (campaignId: string, shortlist_id: string, transcript: string) =>
      request(`/api/campaigns/${campaignId}/submissions/transcript`, {
        method: "POST",
        body: JSON.stringify({ shortlist_id, transcript }),
      }),
    list: (campaignId: string) => request(`/api/campaigns/${campaignId}/reviews`),
    get: (campaignId: string, submissionId: string) =>
      request(`/api/campaigns/${campaignId}/reviews/${submissionId}`),
  },
  compliance: {
    summary: (campaignId: string) =>
      request(`/api/campaigns/${campaignId}/compliance/summary`),
    approvers: (campaignId: string) =>
      request(`/api/campaigns/${campaignId}/approvers`),
    submissionReview: (campaignId: string, submissionId: string) =>
      request(`/api/campaigns/${campaignId}/compliance/submissions/${submissionId}`),
    decide: (
      campaignId: string,
      submissionId: string,
      body: { status: string; actor_id?: string; reviewed_against?: string[]; findings?: object },
    ) =>
      request(`/api/campaigns/${campaignId}/compliance/submissions/${submissionId}/decision`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    nudge: (
      campaignId: string,
      body: { creator_id?: string; submission_id?: string; kind?: string; message?: string },
    ) =>
      request(`/api/campaigns/${campaignId}/compliance/nudge`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    gate: (
      campaignId: string,
      body: {
        gate_type: string; scope?: string; creator_id?: string;
        content_submission_id?: string; decision?: string | null; actor_id?: string;
        context?: object; evidence?: object;
      },
    ) =>
      request(`/api/campaigns/${campaignId}/compliance/gate`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
  },
  audit: {
    get: (campaignId: string) => request(`/api/campaigns/${campaignId}/audit`),
    list: (params: { campaignId?: string; eventType?: string; since?: string } = {}) => {
      const qs = new URLSearchParams();
      if (params.campaignId) qs.set("campaign_id", params.campaignId);
      if (params.eventType) qs.set("event_type", params.eventType);
      if (params.since) qs.set("since", params.since);
      const q = qs.toString();
      return request(`/api/audit${q ? `?${q}` : ""}`);
    },
    logNavigation: (page: string, campaignId?: string, detail?: string) =>
      request(`/api/audit`, {
        method: "POST",
        body: JSON.stringify({ page, campaign_id: campaignId, detail }),
      }),
  },
  discovery: {
    getFilters: (campaignId: string) =>
      request(`/api/campaigns/${campaignId}/discovery/filters`),
    search: (campaignId: string, overrides: object) =>
      request(`/api/campaigns/${campaignId}/discovery/search`, {
        method: "POST",
        body: JSON.stringify(overrides),
      }),
    manualAdd: (campaignId: string, handle: string, platform?: string) =>
      request(`/api/campaigns/${campaignId}/discovery/manual-add`, {
        method: "POST",
        body: JSON.stringify(platform ? { handle, platform } : { handle }),
      }),
    manualAddStatus: (campaignId: string, creatorId: string) =>
      request(`/api/campaigns/${campaignId}/discovery/manual-add/${creatorId}/status`),
    refill: (campaignId: string, platform?: string) =>
      request(
        `/api/campaigns/${campaignId}/discovery/refill${platform ? `?platform=${encodeURIComponent(platform)}` : ""}`,
        { method: "POST" },
      ),
    stats: () => request("/api/discovery/stats"),
    poolCount: (campaignId: string) => request(`/api/campaigns/${campaignId}/discovery/pool-count`),
  },
  outreach: {
    draftEmail: (campaignId: string, entryId: string) =>
      request(`/api/campaigns/${campaignId}/outreach/draft-email`, {
        method: "POST",
        body: JSON.stringify({ entry_id: entryId }),
      }),
    draftFinalizeEmail: (campaignId: string, entryId: string) =>
      request(`/api/campaigns/${campaignId}/outreach/draft-finalize-email`, {
        method: "POST",
        body: JSON.stringify({ entry_id: entryId }),
      }),
  },
  messages: {
    get: (campaignId: string, handle: string) =>
      request(`/api/campaigns/${campaignId}/creators/${encodeURIComponent(handle)}/messages`),
  },
  brand: {
    getProfile: (username: string) =>
      request("/api/brand/profile", { headers: { "x-user": username } }),
    prefill: (url: string, username: string) =>
      request("/api/brand/prefill", { method: "POST", headers: { "x-user": username }, body: JSON.stringify({ url }) }),
    saveProfile: (profile: object, username: string) =>
      request("/api/brand/profile", { method: "POST", headers: { "x-user": username }, body: JSON.stringify(profile) }),
    patchProfile: (fields: object, username: string) =>
      request("/api/brand/profile", { method: "PATCH", headers: { "x-user": username }, body: JSON.stringify(fields) }),
    reset: (username: string) =>
      request("/api/brand/profile/reset", { method: "POST", headers: { "x-user": username } }),
  },
  report: {
    getData: (campaignId: string) =>
      request(`/api/campaigns/${campaignId}/report/data`),
    regenerate: (campaignId: string) =>
      request(`/api/campaigns/${campaignId}/report/regenerate`, { method: "POST" }),
    downloadPdf: async (campaignId: string): Promise<Blob> => {
      const res = await fetch(`${API_BASE}/api/campaigns/${campaignId}/report`);
      if (!res.ok) throw new Error("PDF generation failed");
      return res.blob();
    },
  },
  creators: {
    list: (params: { search?: string; platform?: string; status?: string; campaign_id?: string; niche?: string; owner?: string; page?: number; limit?: number } = {}) => {
      const qs = new URLSearchParams();
      if (params.search) qs.set("search", params.search);
      if (params.platform) qs.set("platform", params.platform);
      if (params.status) qs.set("status", params.status);
      if (params.campaign_id) qs.set("campaign_id", params.campaign_id);
      if (params.niche) qs.set("niche", params.niche);
      if (params.owner) qs.set("owner", params.owner);
      if (params.page) qs.set("page", String(params.page));
      if (params.limit) qs.set("limit", String(params.limit));
      const q = qs.toString();
      return request(`/api/creators${q ? `?${q}` : ""}`);
    },
    campaigns: (owner?: string) => request(`/api/creators/campaigns/list${owner ? `?owner=${encodeURIComponent(owner)}` : ""}`),
    get: (creatorId: string) => request(`/api/creators/${creatorId}`),
    refreshBrandSafety: (creatorId: string) => request(`/api/creators/${creatorId}/brand-safety/refresh`, { method: "POST" }),
    refreshProfileSummary: (creatorId: string) => request(`/api/creators/${creatorId}/profile-summary/refresh`, { method: "POST" }),
    refreshAudienceAuthenticity: (creatorId: string, force = false) =>
      request(`/api/creators/${creatorId}/audience-authenticity/refresh${force ? "?force=true" : ""}`, { method: "POST" }),
  },
  feedback: {
    submit: async (params: { title: string; text: string; feedbackType?: string; pageUrl: string; campaignId?: string; screenshots?: File[]; video?: File | null; media?: File | null }) => {
      const form = new FormData();
      form.append("title", params.title);
      form.append("text", params.text);
      if (params.feedbackType) form.append("feedback_type", params.feedbackType);
      form.append("page_url", params.pageUrl);
      if (params.campaignId) form.append("campaign_id", params.campaignId);
      // Up to 5 screenshots (repeated `screenshots` field) + 1 video.
      (params.screenshots || []).slice(0, 5).forEach((f, i) => form.append("screenshots", f, f.name || `screenshot-${i}`));
      if (params.video) form.append("video", params.video, params.video.name || "video");
      // Legacy single-file field, still honored by the backend.
      if (params.media) form.append("screenshot", params.media, params.media.name || "media");
      const res = await fetch(`${API_BASE}/api/feedback`, { method: "POST", body: form });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: res.statusText }));
        throw new Error(err.detail || "Feedback submission failed");
      }
      return res.json();
    },
  },
};
